import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Mail,
  MoreHorizontal,
  Plus,
  Search,
  ShieldCheck,
  Star,
  TrendingUp,
  User,
  UserPlus,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/store/auth-store";
import {
  type AssignedClient,
  clearPractitionerA2A,
  deletePractitioner,
  DESIGNATION_OPTIONS,
  linkPractitionerToMe,
  listPractitionerAllocation,
  listPractitioners,
  listPractitionerClients,
  type Practitioner,
  type PractitionerOption,
  reassignPractitioner,
  setDefaultPractitioner,
  setSecondaryPractitioner,
} from "@/features/practitioners/api";
import { useFirmContext } from "@/features/clients/hooks/use-firm-context";
import { MemberRoster } from "@/features/practitioners/components/member-roster";
import { usePractitioners } from "@/features/practitioners/hooks/use-practitioners";
import { PractitionerDialog } from "@/features/settings/components/tabs/practitioner-dialog";
import { listMembers, type Member } from "@/features/team/api";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const DESIGNATION_LABELS = Object.fromEntries(
  DESIGNATION_OPTIONS.map((o) => [o.value, o.label]),
);

// Short designation codes for the card's inline badge (full labels are long).
const DESIGNATION_SHORT: Record<Practitioner["designation"], string> = {
  a: "ATTY",
  b: "CPA",
  c: "EA",
  d: "OFFICER",
  e: "FTE",
  f: "FAMILY",
  g: "ACTUARY",
  h: "URP",
  i: "RTRP",
  k: "STUDENT",
  r: "ERPA",
};

type RosterTab = "practitioners" | "directors" | "staff";
type RosterView = "card" | "list";

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

export function PractitionersTab() {
  // Scope the roster/allocation to the active workspace so a house user who has
  // switched into a Business Practitioner sees that firm's practitioners.
  const { firm } = useFirmContext();
  const { practitioners, meta, loading, error, params, setParams, refetch } = usePractitioners({
    firm,
  });
  const [searchInput, setSearchInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Practitioner | undefined>();
  const [removing, setRemoving] = useState<Practitioner | undefined>();
  const [clearingA2A, setClearingA2A] = useState<Practitioner | undefined>();
  const [tab, setTab] = useState<RosterTab>("practitioners");
  const [view, setView] = useState<RosterView>("card");
  const [members, setMembers] = useState<Member[]>([]);
  const [allocation, setAllocation] = useState<Record<string, AssignedClient[]>>({});
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const setUser = useAuthStore((s) => s.setUser);

  // Members (for the role tabs/counts) and per-practitioner client allocation
  // (for the card's "allocated clients" + the KPI) aren't on the roster payload,
  // so pull them alongside. Refreshed after mutations that move clients.
  const loadAux = useCallback(() => {
    listMembers()
      .then(setMembers)
      .catch(() => setMembers([]));
    listPractitionerAllocation(firm)
      .then((rows) => {
        const map: Record<string, AssignedClient[]> = {};
        for (const row of rows) map[row.id] = row.clients;
        setAllocation(map);
      })
      .catch(() => setAllocation({}));
  }, [firm]);

  useEffect(() => {
    loadAux();
  }, [loadAux]);

  // Re-scope the roster when the active workspace changes (sidebar firm switch).
  useEffect(() => {
    setParams({ firm, page: 1 });
  }, [firm, setParams]);

  const refreshAll = useCallback(() => {
    refetch();
    loadAux();
  }, [refetch, loadAux]);

  // Debounce the search box; only push when the trimmed value actually changed.
  useEffect(() => {
    const next = searchInput.trim() || undefined;
    if (next === (params.search ?? undefined)) return;
    const id = setTimeout(() => setParams({ search: next, page: 1 }), 300);
    return () => clearTimeout(id);
  }, [searchInput, params.search, setParams]);

  async function handleSetDefault(p: Practitioner) {
    try {
      await setDefaultPractitioner(p.id);
      refetch();
      toast.success(`${p.fullName} is now the primary practitioner`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't set the default."));
    }
  }

  async function handleSetSecondary(p: Practitioner) {
    try {
      await setSecondaryPractitioner(p.id);
      refetch();
      toast.success(`${p.fullName} is now the secondary practitioner`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't set the secondary."));
    }
  }

  async function handleLink(p: Practitioner) {
    try {
      const linked = await linkPractitionerToMe(p.id);
      setUser({
        ...useAuthStore.getState().user!,
        default_practitioner: linked.id,
        default_practitioner_name: linked.fullName,
      });
      toast.success(`Linked ${p.fullName} to your login`);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't link this practitioner."));
    }
  }

  function openEdit(p?: Practitioner) {
    setEditing(p);
    setDialogOpen(true);
  }

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const rangeStart = meta.count === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + practitioners.length - 1;
  const hasFilters = !!(params.search || params.designation || params.default || params.a2a);

  // Roster KPIs (from the loaded page; the demo roster fits one page).
  const loaded = practitioners.length;
  const ptinOnFile = practitioners.filter((p) => p.ptin).length;
  const a2aEnabled = practitioners.filter((p) => p.esrvUserId).length;
  const ptinMissing = loaded - ptinOnFile;
  const clientsAllocated = new Set(
    Object.values(allocation)
      .flat()
      .map((c) => c.id),
  ).size;
  const kpis = [
    { label: "On roster", value: String(meta.count) },
    {
      label: "PTIN on file",
      value: String(ptinOnFile),
      suffix: `of ${loaded}`,
      tone: ptinOnFile === 0 && loaded > 0 ? "warning" : undefined,
    },
    {
      label: "A2A enabled",
      value: String(a2aEnabled),
      suffix: `of ${loaded}`,
      tone: a2aEnabled === 0 && loaded > 0 ? "muted" : undefined,
    },
    { label: "Clients allocated", value: String(clientsAllocated) },
  ];

  const cdCount = members.filter((m) => m.role === "country_director").length;
  const staffCount = members.filter((m) => m.role === "staff").length;
  const tabs: { key: RosterTab; label: string; count: number }[] = [
    { key: "practitioners", label: "Practitioners", count: meta.count },
    { key: "directors", label: "Country directors", count: cdCount },
    { key: "staff", label: "Staff", count: staffCount },
  ];

  const subtitle = (
    <>
      The credentialed professionals who represent your clients before the IRS —{" "}
      <strong className="text-foreground font-semibold">{meta.count} on the roster</strong>
      {ptinMissing > 0 ? (
        <>
          ,{" "}
          <strong className="text-warning font-semibold">
            {ptinMissing} PTIN missing
          </strong>
          .
        </>
      ) : (
        "."
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-7">
      <PageHeader
        eyebrow="Workspace · People"
        title="Practitioners"
        subtitle={subtitle}
        actions={
          <>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/practitioners/allocation">
                <TrendingUp className="size-4" />
                Client allocation
              </Link>
            </Button>
            {isAdmin && (
              <Button type="button" variant="cta" className="gap-2" onClick={() => openEdit()}>
                <Plus className="size-4" />
                Add practitioner
              </Button>
            )}
          </>
        }
      />

      {/* Role tabs + (practitioner) controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="bg-muted inline-flex flex-wrap gap-1 rounded-sm p-1">
          {tabs.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => setTab(t.key)}
                className={cn(
                  "inline-flex h-9 items-center gap-2 rounded-[10px] px-4 text-[13px] font-medium transition-colors",
                  active
                    ? "bg-brand-panel font-semibold text-white"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
                <span
                  className={cn(
                    "rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums",
                    active ? "bg-white/20 text-white" : "bg-card text-muted-foreground",
                  )}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {tab === "practitioners" ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Name, email or firm…"
                className="pl-9"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Select
              value={params.designation ?? "all"}
              onValueChange={(v) =>
                setParams({
                  designation: v === "all" ? undefined : (v as Practitioner["designation"]),
                  page: 1,
                })
              }
            >
              <SelectTrigger size="sm" className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All designations</SelectItem>
                {DESIGNATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={params.a2a ?? "all"}
              onValueChange={(v) =>
                setParams({ a2a: v === "all" ? undefined : (v as "connected" | "none"), page: 1 })
              }
            >
              <SelectTrigger size="sm" className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">A2A status</SelectItem>
                <SelectItem value="connected">A2A connected</SelectItem>
                <SelectItem value="none">No A2A</SelectItem>
              </SelectContent>
            </Select>
            <div className="bg-muted flex gap-0.5 rounded-sm p-0.5">
              <Button
                type="button"
                size="icon-sm"
                variant={view === "card" ? "outline" : "ghost"}
                aria-label="Card view"
                aria-pressed={view === "card"}
                className={view === "card" ? "bg-card" : "border-transparent shadow-none"}
                onClick={() => setView("card")}
              >
                <LayoutGrid />
              </Button>
              <Button
                type="button"
                size="icon-sm"
                variant={view === "list" ? "outline" : "ghost"}
                aria-label="List view"
                aria-pressed={view === "list"}
                className={view === "list" ? "bg-card" : "border-transparent shadow-none"}
                onClick={() => setView("list")}
              >
                <List />
              </Button>
            </div>
          </div>
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link to="/team">Manage in Team</Link>
          </Button>
        )}
      </div>

      {tab === "directors" && <MemberRoster role="country_director" members={members} />}
      {tab === "staff" && <MemberRoster role="staff" members={members} />}

      {tab === "practitioners" &&
        (error ? (
          <p className="text-destructive text-sm">
            Couldn&apos;t load this section. Please refresh and try again.
          </p>
        ) : loading && practitioners.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <>
            {/* Roster KPI band */}
            <Card className="grid grid-cols-2 gap-0 overflow-hidden p-0 sm:grid-cols-4">
              {kpis.map((kpi, i) => (
                <div
                  key={kpi.label}
                  className={cn(
                    "flex flex-col gap-1.5 px-6 py-[1.125rem]",
                    i > 0 && "border-t sm:border-t-0",
                    i < kpis.length - 1 && "sm:border-r",
                  )}
                >
                  <span className="text-muted-foreground text-xs font-semibold tracking-[0.08em] uppercase">
                    {kpi.label}
                  </span>
                  <span className="flex items-baseline gap-2">
                    <span
                      className={cn(
                        "font-heading text-[1.875rem] leading-none font-semibold tabular-nums",
                        kpi.tone === "warning" && "text-warning",
                        kpi.tone === "muted" && "text-muted-foreground",
                      )}
                    >
                      {kpi.value}
                    </span>
                    {kpi.suffix && (
                      <span className="text-muted-foreground text-xs">{kpi.suffix}</span>
                    )}
                  </span>
                </div>
              ))}
            </Card>

            {practitioners.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {hasFilters
                  ? "No practitioners match these filters."
                  : `No practitioners yet.${isAdmin ? " Add one to start representing clients." : ""}`}
              </p>
            ) : view === "card" ? (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {practitioners.map((p) => (
                  <PractitionerCard
                    key={p.id}
                    practitioner={p}
                    clients={allocation[p.id] ?? []}
                    isAdmin={isAdmin}
                    onEdit={() => openEdit(p)}
                    onSetDefault={() => handleSetDefault(p)}
                    onSetSecondary={() => handleSetSecondary(p)}
                    onLink={() => handleLink(p)}
                    onClearA2A={() => setClearingA2A(p)}
                    onRemove={() => setRemoving(p)}
                  />
                ))}
                {isAdmin && <AddPractitionerCard onClick={() => openEdit()} />}
              </div>
            ) : (
              <div className="bg-card divide-y overflow-hidden rounded-xl border">
                {practitioners.map((p) => (
                  <PractitionerRow
                    key={p.id}
                    practitioner={p}
                    isAdmin={isAdmin}
                    onEdit={() => openEdit(p)}
                    onSetDefault={() => handleSetDefault(p)}
                    onSetSecondary={() => handleSetSecondary(p)}
                    onLink={() => handleLink(p)}
                    onClearA2A={() => setClearingA2A(p)}
                    onRemove={() => setRemoving(p)}
                  />
                ))}
              </div>
            )}

            {/* Pagination footer */}
            {practitioners.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-muted-foreground text-sm">
                  Showing{" "}
                  <span className="text-foreground font-medium">
                    {rangeStart}–{rangeEnd}
                  </span>{" "}
                  of <span className="text-foreground font-medium">{meta.count}</span> practitioners
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">Rows per page:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(v) => setParams({ pageSize: Number(v), page: 1 })}
                    >
                      <SelectTrigger size="sm" className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={!meta.previous}
                      onClick={() => setParams({ page: page - 1 })}
                      aria-label="Previous page"
                    >
                      <ChevronLeft />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      disabled={!meta.next}
                      onClick={() => setParams({ page: page + 1 })}
                      aria-label="Next page"
                    >
                      <ChevronRight />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        ))}

      {/* Roles explainer */}
      <Card className="gap-4 p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Roles in this workspace</h2>
          <Link to="/team" className="text-info text-[13px] font-semibold hover:underline">
            Manage
          </Link>
        </div>
        <ul className="flex flex-col gap-2">
          {[
            {
              icon: <TrendingUp className="size-4" />,
              tone: "bg-brand-panel text-white",
              role: "Country director",
              desc: "Oversees all practitioners and clients in a country · approves allocations.",
              count: cdCount,
            },
            {
              icon: <ShieldCheck className="size-4" />,
              tone: "bg-accent text-info",
              role: "Practitioner",
              desc: "Represents clients before the IRS · signs POAs, pulls transcripts.",
              count: meta.count,
            },
            {
              icon: <User className="size-4" />,
              tone: "bg-muted text-muted-foreground",
              role: "Staff",
              desc: "Case work, documents and client communication under a practitioner.",
              count: staffCount,
            },
          ].map((r) => (
            <li
              key={r.role}
              className="bg-surface-hover flex items-center gap-3 rounded-xl p-3"
            >
              <span
                className={cn(
                  "flex size-9 shrink-0 items-center justify-center rounded-sm",
                  r.tone,
                )}
              >
                {r.icon}
              </span>
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="text-[13px] font-semibold">{r.role}</span>
                <span className="text-muted-foreground text-xs">{r.desc}</span>
              </span>
              <span className="text-muted-foreground bg-card shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap tabular-nums">
                {r.count} assigned
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <PractitionerDialog
        key={editing?.id ?? "new"}
        practitioner={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refreshAll}
      />
      {removing && (
        <RemovePractitionerDialog
          practitioner={removing}
          onClose={() => setRemoving(undefined)}
          onRemoved={refreshAll}
        />
      )}
      {clearingA2A && (
        <ClearA2ADialog
          practitioner={clearingA2A}
          onClose={() => setClearingA2A(undefined)}
          onCleared={refreshAll}
        />
      )}
    </div>
  );
}

interface CardActionProps {
  practitioner: Practitioner;
  isAdmin: boolean;
  onEdit: () => void;
  onSetDefault: () => void;
  onSetSecondary: () => void;
  onLink: () => void;
  onClearA2A: () => void;
  onRemove: () => void;
}

function PractitionerCard({
  practitioner: p,
  clients,
  ...actions
}: CardActionProps & { clients: AssignedClient[] }) {
  const designationLabel = DESIGNATION_LABELS[p.designation] ?? p.designation;
  const incomplete = !p.ptin || !p.cafNumber;
  const extraClients = clients.length - 3;

  const creds = [
    {
      label: "PTIN",
      value: p.ptin || "Missing",
      tone: p.ptin ? "text-foreground" : "text-warning",
      dot: p.ptin ? null : "bg-primary",
    },
    {
      label: "A2A access",
      value: p.esrvUserId ? "Connected" : "Not set up",
      tone: p.esrvUserId ? "text-foreground" : "text-muted-foreground",
      dot: p.esrvUserId ? "bg-success" : "bg-muted-foreground/50",
    },
    { label: "CAF number", value: p.cafNumber || "—", tone: "text-foreground", dot: null },
  ];

  return (
    <Card className="relative gap-0 overflow-hidden p-0">
      {/* Navy header strip */}
      <div className="bg-brand-panel relative overflow-hidden px-6 pt-5 pb-14 text-white">
        <div className="bg-primary/25 pointer-events-none absolute -top-16 -right-12 size-44 rounded-full blur-2xl" />
        <div className="relative flex items-center justify-between gap-2">
          <span className="text-brand-panel-muted inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.1em] uppercase">
            <ShieldCheck className="text-primary size-3.5" />
            Practitioner
          </span>
          {p.defaultDesignation === "PRIMARY" ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">
              <Star className="fill-primary text-primary size-3" />
              Default for new clients
            </span>
          ) : p.defaultDesignation === "SECONDARY" ? (
            <span className="shrink-0 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">
              Secondary
            </span>
          ) : null}
        </div>
      </div>

      {/* Avatar + status, overlapping the strip */}
      <div className="relative -mt-9 flex items-end justify-between gap-3 px-6">
        <span className="bg-card size-[72px] rounded-[22px] p-1 shadow-[0_6px_16px_-8px_rgb(18_26_46_/_0.4)]">
          <span className="bg-accent text-info font-heading flex size-full items-center justify-center rounded-[18px] text-[22px] font-bold">
            {initials(p.fullName)}
          </span>
        </span>
        <span className="text-success border-border bg-card mb-1.5 inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold">
          <span className="bg-success size-1.5 rounded-full" />
          Active
        </span>
      </div>

      {/* Identity */}
      <div className="flex flex-col gap-1 px-6 pt-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3 className="font-heading text-[1.375rem] font-semibold tracking-[-0.015em]">
            {p.fullName}
          </h3>
          <span className="bg-accent text-info rounded-full px-2 py-0.5 text-[11px] font-bold tracking-[0.06em] uppercase">
            {DESIGNATION_SHORT[p.designation] ?? p.designation}
          </span>
        </div>
        <span className="text-muted-foreground text-[13px]">
          {designationLabel}
          {p.firmName ? ` · ${p.firmName}` : ""}
        </span>
        {p.email ? (
          <a
            href={`mailto:${p.email}`}
            className="text-info mt-0.5 inline-flex items-center gap-1.5 text-[13px] break-all"
          >
            <Mail className="size-3.5 shrink-0" />
            {p.email}
          </a>
        ) : (
          <span className="text-muted-foreground mt-0.5 text-[13px]">No email</span>
        )}
      </div>

      {/* Credentials */}
      <dl className="mx-6 mt-4 grid grid-cols-3 gap-3 border-y py-3.5">
        {creds.map((c) => (
          <div key={c.label} className="flex flex-col gap-1">
            <dt className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
              {c.label}
            </dt>
            <dd className={cn("inline-flex items-center gap-1.5 text-[13px] font-semibold", c.tone)}>
              {c.dot && <span className={cn("size-1.5 rounded-full", c.dot)} />}
              {c.value}
            </dd>
          </div>
        ))}
      </dl>

      {/* Allocated clients */}
      <div className="flex items-center justify-between gap-3 px-6 pt-3.5">
        <div className="flex flex-col gap-0.5 leading-tight">
          <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.06em] uppercase">
            Allocated clients
          </span>
          <span className="text-[13px] font-semibold">
            <span className="tabular-nums">{clients.length}</span>{" "}
            {clients.length === 1 ? "client" : "clients"} ·{" "}
            <span className="tabular-nums">{p.linkedUserCount}</span> linked{" "}
            {p.linkedUserCount === 1 ? "user" : "users"}
          </span>
        </div>
        <div className="flex items-center">
          {clients.slice(0, 3).map((c, i) => (
            <span
              key={c.id}
              className={cn(
                "bg-accent text-info border-card font-heading flex size-8 items-center justify-center rounded-[10px] border-2 text-[11px] font-bold",
                i > 0 && "-ml-1.5",
              )}
              title={c.displayName}
            >
              {initials(c.displayName)}
            </span>
          ))}
          {extraClients > 0 && (
            <span className="bg-muted text-muted-foreground border-card font-heading -ml-1.5 flex size-8 items-center justify-center rounded-[10px] border-2 text-[11px] font-bold">
              +{extraClients}
            </span>
          )}
          <Link
            to="/practitioners/allocation"
            aria-label="Allocate a client"
            className="border-input text-muted-foreground hover:text-foreground -ml-1.5 flex size-8 items-center justify-center rounded-[10px] border border-dashed"
          >
            <Plus className="size-3.5" />
          </Link>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto flex gap-2 px-6 pt-[1.125rem] pb-5">
        {actions.isAdmin ? (
          <>
            <Button
              type="button"
              className="bg-brand-panel hover:bg-brand-panel/90 flex-1 text-white"
              onClick={actions.onEdit}
            >
              View profile
            </Button>
            {incomplete && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={actions.onEdit}
              >
                Complete credentials
              </Button>
            )}
          </>
        ) : (
          <span className="flex-1" />
        )}
        <RowActions {...actions} practitioner={p} />
      </div>
    </Card>
  );
}

function AddPractitionerCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-input bg-card/50 hover:border-info hover:bg-surface-hover flex min-h-80 flex-col items-center justify-center gap-3.5 rounded-xl border border-dashed p-7 text-center transition-colors"
    >
      <span className="bg-card text-info border-border flex size-16 items-center justify-center rounded-xl border shadow-[0_6px_16px_-10px_rgb(18_26_46_/_0.3)]">
        <UserPlus className="size-6" />
      </span>
      <span className="flex flex-col gap-1">
        <span className="font-heading text-lg font-semibold">Add a practitioner</span>
        <span className="text-muted-foreground max-w-xs text-[13px]">
          Enter their designation, PTIN and CAF number, then link an e-Services login. They&apos;ll
          appear here with their own credential card.
        </span>
      </span>
      <span className="bg-primary text-primary-foreground inline-flex h-10 items-center gap-2 rounded-sm px-4 text-[13px] font-semibold shadow-[0_8px_20px_-8px_rgb(242_140_40_/_0.7)]">
        <Plus className="size-4" />
        Add practitioner
      </span>
    </button>
  );
}

function PractitionerRow({ practitioner: p, ...actions }: CardActionProps) {
  return (
    <div className="flex items-center gap-4 px-5 py-3.5">
      <span className="bg-accent text-info font-heading flex size-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
        {initials(p.fullName)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold">{p.fullName}</span>
          {p.defaultDesignation === "PRIMARY" && (
            <Star className="fill-primary text-primary size-3.5 shrink-0" />
          )}
        </div>
        <span className="text-muted-foreground truncate text-xs">
          {DESIGNATION_LABELS[p.designation] ?? p.designation}
          {p.firmName ? ` · ${p.firmName}` : ""}
        </span>
      </div>
      <span
        className={cn(
          "hidden text-[13px] font-medium sm:inline-flex sm:w-28",
          p.ptin ? "text-foreground" : "text-warning",
        )}
      >
        {p.ptin || "PTIN missing"}
      </span>
      <span className="text-muted-foreground hidden w-24 text-[13px] lg:inline-flex">
        {p.esrvUserId ? "A2A connected" : "No A2A"}
      </span>
      <div className="flex items-center gap-2">
        {actions.isAdmin && (
          <Button type="button" size="sm" variant="outline" onClick={actions.onEdit}>
            View profile
          </Button>
        )}
        <RowActions {...actions} practitioner={p} />
      </div>
    </div>
  );
}

function RowActions({
  practitioner,
  isAdmin,
  onEdit,
  onSetDefault,
  onSetSecondary,
  onLink,
  onClearA2A,
  onRemove,
}: CardActionProps) {
  const hasA2A = !!practitioner.esrvUserId;
  // A non-admin with no A2A practitioner to link has no available actions.
  if (!isAdmin && !hasA2A) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="icon" variant="outline" aria-label="Practitioner actions">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isAdmin && <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>}
        {isAdmin && practitioner.defaultDesignation !== "PRIMARY" && (
          <DropdownMenuItem onSelect={onSetDefault}>Set as primary</DropdownMenuItem>
        )}
        {isAdmin && practitioner.defaultDesignation !== "SECONDARY" && (
          <DropdownMenuItem onSelect={onSetSecondary}>Set as secondary</DropdownMenuItem>
        )}
        {hasA2A && <DropdownMenuItem onSelect={onLink}>Link to my login</DropdownMenuItem>}
        {isAdmin && hasA2A && (
          <DropdownMenuItem onSelect={onClearA2A}>Clear A2A user ID</DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onRemove}>
              Remove
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const REASSIGN_NONE = "__none__";

function RemovePractitionerDialog({
  practitioner,
  onClose,
  onRemoved,
}: {
  practitioner: Practitioner;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [others, setOthers] = useState<PractitionerOption[]>([]);
  const [reassignTo, setReassignTo] = useState<string>(REASSIGN_NONE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listPractitionerClients(practitioner.id)
      .then((clients) => active && setClientCount(clients.length))
      .catch(() => active && setClientCount(0));
    // Reassign targets span the whole firm, not just the current page.
    listPractitioners()
      .then((list) => active && setOthers(list.filter((p) => p.id !== practitioner.id)))
      .catch(() => active && setOthers([]));
    return () => {
      active = false;
    };
  }, [practitioner.id]);

  const assigned = (clientCount ?? 0) > 0;

  async function confirm() {
    if (assigned && reassignTo === REASSIGN_NONE) return;
    setBusy(true);
    try {
      if (assigned) {
        await reassignPractitioner(practitioner.id, reassignTo);
      } else {
        await deletePractitioner(practitioner.id);
      }
      onRemoved();
      toast.success("Practitioner removed");
      onClose();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't remove the practitioner."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {practitioner.fullName}?</DialogTitle>
          <DialogDescription>
            {clientCount === null
              ? "Checking client assignments…"
              : assigned
                ? `They're assigned to ${clientCount} ${clientCount === 1 ? "client" : "clients"}. Choose another practitioner to take over before removing them.`
                : "They'll no longer be selectable for new forms. Existing forms that reference them are unaffected."}
          </DialogDescription>
        </DialogHeader>
        {assigned && (
          <div className="grid gap-2">
            <Label htmlFor="reassign-to">Reassign clients to</Label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger id="reassign-to" className="w-full">
                <SelectValue placeholder="Select a practitioner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REASSIGN_NONE} disabled>
                  Select a practitioner
                </SelectItem>
                {others.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-destructive text-white hover:bg-destructive/90"
            disabled={busy || clientCount === null || (assigned && reassignTo === REASSIGN_NONE)}
            onClick={confirm}
          >
            {assigned ? "Reassign & remove" : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClearA2ADialog({
  practitioner,
  onClose,
  onCleared,
}: {
  practitioner: Practitioner;
  onClose: () => void;
  onCleared: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      await clearPractitionerA2A(practitioner.id);
      onCleared();
      toast.success("A2A user ID cleared");
      onClose();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't clear the A2A user ID."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clear A2A user ID for {practitioner.fullName}?</DialogTitle>
          <DialogDescription>
            This removes the e-Services connection. Any user whose default practitioner is set to
            this one will have that binding cleared and must re-link.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={(c) => setAcknowledged(c === true)}
            className="mt-0.5"
          />
          I understand this unlinks any user defaulting to this practitioner.
        </label>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-destructive text-white hover:bg-destructive/90"
            disabled={!acknowledged || busy}
            onClick={confirm}
          >
            Clear A2A ID
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
